import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { LoadingPanel } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { Modal } from '../albums/Modal';

/**
 * What hosts have said about one of your tracks.
 *
 * ## The half of reviews that did not exist
 *
 * Hosts have been able to review a track since the console shipped, and until
 * now the artist had nowhere to read it. The notification told them once and
 * then it was gone - which is the same as not having written anything, from the
 * side that matters.
 *
 * ## The badge is the control
 *
 * A star average that is not clickable is a score, and a score is the least
 * interesting thing here: a number out of five tells an artist nothing they can
 * act on, and the sentence underneath it might. So the badge exists to be
 * pressed, and it is only drawn when there is something behind it.
 *
 * ## The owner sees this and nobody else
 *
 * Enforced on the server, which returns 404 for anybody else - the note on
 * `ReceivedReviewsAction` explains why sending a review is not publishing one.
 * The list simply never asks for a profile that is not yours, because the
 * summary it reads is not sent to a visitor at all.
 */
interface Review {
  id: number;
  rating: number | null;
  body: string | null;
  at: string;
  host: { username: string; display_name: string; avatar: string | null };
  station: string | null;
}

export function ReceivedReviews({
  trackId,
  title,
  summary,
}: {
  trackId: number;
  title: string;
  /** From the track list. Absent when no host has sent anything. */
  summary: { count: number; average: number | null } | null;
}) {
  const [open, setOpen] = useState(false);

  if (!summary || summary.count === 0) return null;

  return (
    <>
      <button
        type="button"
        data-reviews={trackId}
        onClick={() => setOpen(true)}
        title={`${summary.count} host ${summary.count === 1 ? 'review' : 'reviews'}`}
        className="flex shrink-0 items-center gap-1 rounded-[var(--radius-pill)] border border-line px-2.5 py-1 text-soft transition-colors hover:border-line-strong hover:text-ink"
      >
        <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 text-warm" fill="currentColor" aria-hidden="true">
          <path d="M10 2.6l2.1 4.6 5 .5-3.7 3.4 1 4.9-4.4-2.5-4.4 2.5 1-4.9L2.9 7.7l5-.5z" />
        </svg>

        <span className="font-mono text-[11px] tabular-nums">
          {/*
            The average when hosts gave one, the count when they only wrote
            words. A track with two written reviews and no scores would
            otherwise show a badge with nothing in it.
          */}
          {summary.average ?? summary.count}
        </span>
      </button>

      {open && (
        <ReviewsModal trackId={trackId} title={title} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function ReviewsModal({
  trackId,
  title,
  onClose,
}: {
  trackId: number;
  title: string;
  onClose: () => void;
}) {
  const { data, isPending } = useQuery({
    queryKey: ['track-reviews', trackId],
    queryFn: () =>
      api.get<{ items: Review[]; count: number; average: number | null }>(
        `/tracks/${trackId}/reviews`,
      ),
  });

  return (
    <Modal title={`Host reviews — ${title}`} onClose={onClose}>
      {isPending ? (
        <LoadingPanel label="Loading reviews" />
      ) : (
        <div className="flex flex-col gap-5">
          {data?.average !== null && data?.average !== undefined && (
            <p className="text-sm text-soft">
              <span className="font-mono text-lg text-ink">{data.average}</span> out of 5, across{' '}
              {data.count} {data.count === 1 ? 'review' : 'reviews'}.
            </p>
          )}

          <ul className="flex flex-col gap-5">
            {(data?.items ?? []).map((review) => (
              <li key={review.id} className="flex gap-3" data-review={review.id}>
                {review.host.avatar ? (
                  <img
                    src={review.host.avatar}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-[var(--radius-pill)] object-cover"
                  />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-raised text-xs font-bold text-soft">
                    {(review.host.display_name || review.host.username).charAt(0).toUpperCase()}
                  </span>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-medium text-ink">
                      {review.host.display_name || review.host.username}
                    </span>

                    {/* The room, when it has a name. Most sets are just
                        somebody going live, and nothing is missing when it
                        is absent. */}
                    {review.station && (
                      <span className="meta text-faint">{review.station}</span>
                    )}

                    {review.rating !== null && (
                      <span className="ml-auto font-mono text-[11px] tabular-nums text-warm">
                        {'★'.repeat(review.rating)}
                        <span className="text-ghost">{'★'.repeat(5 - review.rating)}</span>
                      </span>
                    )}
                  </div>

                  {review.body ? (
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-soft">
                      {review.body}
                    </p>
                  ) : (
                    /*
                      A score with no words is a real thing a host can send, and
                      saying so is better than an empty space that looks like
                      something failed to load.
                    */
                    <p className="mt-1 text-sm italic text-faint">Rated, without a note.</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
